import { useT } from "@transifex/react";
import { useState } from "react";
import CreateInfoModal from "../../../basic/create/InfoModal/CreateInfoModal";
import VocabTrainingLimitModal from "../../../basic/create/vocabTrainingLimitModal/VocabTrainingLimitModal";
import type { ClassActivity } from "../../../complex/classActivityCard/ClassActivityCardsOverview.types";
import { CreationModes } from "../../../constants/CreationFlowConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { useExistingActivitySelection } from "../../../hooks/useExistingActivitySelection";
import { useLogger } from "../../../hooks/useLogger";
import ExistingActivityList from "./ExistingActivityList";

import {
    DisabledWhenEditingSpan,
    RecentBooksMainWrapper,
    SourceOptionArrowWrapper,
    SourceOptionIconWrapper,
    SourceOptionItem,
    SourceOptionItemDesc,
    SourceOptionItemTitle,
    SourceOptionItemTitleWrapper,
    StyledArrowIcon,
    StyledP6Icon,
} from "./StyledComponents";

export interface ExistingActivityContentProps {
    hasActivities: boolean;
    isDisabled?: boolean;
    disabledLabel?: string;
}

const ExistingActivityContent = (props: ExistingActivityContentProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [activityPendingConfirmation, setActivityPendingConfirmation] = useState<ClassActivity | undefined>();

    const {
        loadActivityIntoSelection,
        isLoadingActivity,
        hasPendingLargeSelection,
        confirmLargeSelection,
        cancelLargeSelection,
    } = useExistingActivitySelection();
    const { vocabularyDrawerContent } = useTestCreationContext();
    const { creatingMode } = useGeneralContext();
    const { logEvent } = useLogger();

    const t = useT();
    const t_title = t("From existing activity", { _tags: "CreateTest,ExistingActivityContent" });
    const t_description = t("Vocabulary from another task or a vocabulary training", {
        _tags: "CreateTest,ExistingActivityContent",
    });
    const t_replaceWarning = t("Changing the current book might delete the current selected content.", {
        _tags: "CreateTest,ExistingActivityContent",
    });
    const t_replaceWarning2 = t("Are you sure you want to continue?", {
        _tags: "CreateTest,ExistingActivityContent",
    });
    const t_continue = t("Continue", { _tags: "CreateTest,ExistingActivityContent" });

    if (!props.hasActivities) {
        return null;
    }

    const startLoadingActivity = (activity: ClassActivity) => {
        if (creatingMode === CreationModes.TEST) {
            logEvent("Source Type Selected", { source_type: "existing_activity" });
        }
        loadActivityIntoSelection(activity);
    };

    const handleActivityClick = (activity: ClassActivity) => {
        if (isLoadingActivity) return;
        if (creatingMode === CreationModes.TEST && Object.keys(vocabularyDrawerContent).length > 0) {
            setActivityPendingConfirmation(activity);
            return;
        }
        startLoadingActivity(activity);
    };

    const closeConfirmation = () => setActivityPendingConfirmation(undefined);

    const confirmActivityReplacement = () => {
        const activity = activityPendingConfirmation;
        closeConfirmation();
        if (activity) startLoadingActivity(activity);
    };

    const renderHeader = () => (
        <SourceOptionItem
            data-testid="source-option-existing-activity"
            onClick={() => {
                if (props.isDisabled) return;
                setIsExpanded((val) => !val);
            }}
            className={isExpanded ? "orange" : ""}
            noBorder
        >
            <SourceOptionIconWrapper>
                <StyledP6Icon name={"documents"} />
            </SourceOptionIconWrapper>
            <SourceOptionItemTitleWrapper>
                <SourceOptionItemTitle>
                    {t_title}
                    {props.isDisabled && props.disabledLabel && (
                        <>
                            <br />
                            <DisabledWhenEditingSpan>{props.disabledLabel}</DisabledWhenEditingSpan>
                        </>
                    )}
                </SourceOptionItemTitle>
                <SourceOptionItemDesc>{t_description}</SourceOptionItemDesc>
            </SourceOptionItemTitleWrapper>
            <SourceOptionArrowWrapper>
                <StyledArrowIcon
                    onClick={() => {}}
                    direction={isExpanded ? "BOTTOM" : "RIGHT"}
                />
            </SourceOptionArrowWrapper>
        </SourceOptionItem>
    );

    const renderReplaceConfirmation = () => (
        <CreateInfoModal
            isOpen={!!activityPendingConfirmation}
            onRequestClose={closeConfirmation}
            continueAction={confirmActivityReplacement}
            continueText={t_continue}
            cancelAction={closeConfirmation}
            compact
        >
            {t_replaceWarning}
            <br />
            {t_replaceWarning2}
        </CreateInfoModal>
    );

    return (
        <RecentBooksMainWrapper className={`recentBooks ${props.isDisabled ? "disabled" : ""}`}>
            {renderHeader()}
            {isExpanded && (
                <ExistingActivityList
                    onSelectActivity={handleActivityClick}
                    isSelectionDisabled={isLoadingActivity}
                />
            )}
            {renderReplaceConfirmation()}
            <VocabTrainingLimitModal
                isOpen={hasPendingLargeSelection}
                onConfirm={confirmLargeSelection}
                onCancel={cancelLargeSelection}
            />
        </RecentBooksMainWrapper>
    );
};

export default ExistingActivityContent;
